import {
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  DeleteOutline,
  EditOutlined,
  CancelOutlined,
  ReplayOutlined,
} from '@mui/icons-material';
import React, { useEffect, useState } from 'react';
import { useSnackBar } from '../../../hooks/useSnackBar';
import useAuth from '../../../hooks/useAuth';
import { useTranslation } from '../../../hooks/useTranslation';
import { UserPermissions } from '../../../interfaces/User';
import {
  BulkInviteSucceededItem,
  getInvites,
  getUsers,
  deleteInvite,
  resendInvite,
  revokeInvite,
  RosterEntry,
  RosterInvite,
  RosterMember,
} from '../../../services/inviteService';
import { deleteUser, updateUser } from '../../../services/userService';
import { TranslationKey } from '../../../types/i18n';
import { PERMISSIONS } from '../../../utils/permissions';
import Button from '../../shared/Button/Button';
import StyledTableColumnHeader from '../../shared/Table/StyledTableColumnHeader';
import BulkInviteModal from './BulkInviteModal';

const PERMISSION_LABELS: [keyof UserPermissions, TranslationKey][] = [
  [PERMISSIONS.MANAGE_AGENTS, 'Manage Agents'],
  [PERMISSIONS.MANAGE_KNOWLEDGE, 'Manage Knowledge'],
  [PERMISSIONS.VIEW_AGENTS_DASHBOARD, 'View Dashboard'],
  [PERMISSIONS.MANAGE_BILLING, 'Manage Billing'],
  [PERMISSIONS.MANAGE_USERS, 'Manage Users'],
  [PERMISSIONS.MANAGE_INTEGRATIONS, 'Manage Integrations'],
];

interface UserManagementPanelProps {
  showTitle?: boolean;
}

const StatusChip: React.FC<{
  status: string;
  t: (k: TranslationKey) => string;
}> = ({ status, t }) => {
  if (status === 'accepted')
    return <Chip label={t('Accepted')} color="success" size="small" />;
  if (status === 'pending')
    return <Chip label={t('Pending')} color="primary" size="small" />;
  return <Chip label={t('Expired')} color="warning" size="small" />;
};

const UserManagementPanel: React.FC<UserManagementPanelProps> = ({
  showTitle = true,
}) => {
  const { t } = useTranslation();
  const showSnackbar = useSnackBar();
  const { currentUser } = useAuth();
  const actionIconButtonStyle = {
    minWidth: '44px',
    width: '44px',
    height: '44px',
    borderRadius: '10px',
    background: '#F1F5F9',
    borderColor: 'rgba(15, 23, 42, 0.06)',
    color: '#1841ff',
    padding: '0.5rem',
  };

  const [roster, setRoster] = useState<RosterEntry[]>([]);
  const [loading, setLoading] = useState(true);

  const [bulkInviteOpen, setBulkInviteOpen] = useState(false);

  const [editMember, setEditMember] = useState<RosterMember | null>(null);
  const [editPermissions, setEditPermissions] = useState<UserPermissions>({});
  const [saving, setSaving] = useState(false);

  const [removeMember, setRemoveMember] = useState<RosterMember | null>(null);
  const [removing, setRemoving] = useState(false);

  useEffect(() => {
    Promise.all([getUsers(), getInvites()])
      .then(([members, invites]) => {
        setRoster([...members, ...invites]);
      })
      .catch(() => {
        showSnackbar(t('Failed to load roster.'), 'error');
      })
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleBulkInviteSuccess = (succeeded: BulkInviteSucceededItem[]) => {
    if (succeeded.length === 0) return;
    const newInvites: RosterInvite[] = succeeded.map(item => ({
      entry_type: 'invite',
      id: item.id,
      email: item.email,
      invited_by_name: '',
      created_at: new Date().toISOString(),
      expires_at: item.expires_at,
      roster_status: 'pending',
    }));
    setRoster(prev => [...prev, ...newInvites]);
  };

  const handleRevoke = async (invite: RosterInvite) => {
    const prev = roster;
    setRoster(r =>
      r.map(e =>
        e.id === invite.id
          ? ({ ...e, roster_status: 'expired' } as RosterEntry)
          : e
      )
    );
    try {
      await revokeInvite(invite.id);
      showSnackbar(t('Invite revoked.'), 'success');
    } catch {
      setRoster(prev);
      showSnackbar(t('Failed to revoke invite.'), 'error');
    }
  };

  const handleDeleteInvite = async (invite: RosterInvite) => {
    const companyId = currentUser?.company_id;
    if (!companyId) {
      showSnackbar(t('Failed to delete invite.'), 'error');
      return;
    }

    const prev = roster;
    setRoster(r => r.filter(e => e.id !== invite.id));
    try {
      await deleteInvite(invite.id, companyId);
      showSnackbar(t('Invite deleted.'), 'success');
    } catch {
      setRoster(prev);
      showSnackbar(t('Failed to delete invite.'), 'error');
    }
  };

  const handleResend = async (invite: RosterInvite) => {
    const prev = roster;
    setRoster(r =>
      r.map(e =>
        e.id === invite.id
          ? ({ ...e, roster_status: 'pending' } as RosterEntry)
          : e
      )
    );
    try {
      const result = await resendInvite(invite.id);
      setRoster(r =>
        r.map(e =>
          e.id === invite.id
            ? ({
                ...e,
                roster_status: 'pending',
                expires_at: result.expires_at,
              } as RosterEntry)
            : e
        )
      );
      showSnackbar(t('Invite resent.'), 'success');
    } catch {
      setRoster(prev);
      showSnackbar(t('Failed to resend invite.'), 'error');
    }
  };

  const handleOpenEdit = (member: RosterMember) => {
    setEditMember(member);
    setEditPermissions({ ...member.permissions });
  };

  const handlePermissionToggle = (key: keyof UserPermissions) => {
    setEditPermissions(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSavePermissions = async () => {
    if (!editMember) return;
    setSaving(true);
    try {
      const settablePermissions: UserPermissions = Object.fromEntries(
        PERMISSION_LABELS.map(([key]) => [key, Boolean(editPermissions[key])])
      );
      await updateUser(editMember.id, { permissions: settablePermissions });
      setRoster(r =>
        r.map(e =>
          e.id === editMember.id
            ? { ...e, permissions: settablePermissions }
            : e
        )
      );
      showSnackbar(t('Permissions updated.'), 'success');
      setEditMember(null);
    } catch (error: any) {
      showSnackbar(
        error?.message || t('Failed to update permissions.'),
        'error'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    if (!removeMember) return;
    setRemoving(true);
    try {
      await deleteUser(removeMember.id);
      setRoster(r => r.filter(e => e.id !== removeMember.id));
      showSnackbar(t('User removed.'), 'success');
      setRemoveMember(null);
    } catch {
      showSnackbar(t('Failed to remove user.'), 'error');
    } finally {
      setRemoving(false);
    }
  };

  const permissionSummary = (permissions: UserPermissions): string => {
    const active = PERMISSION_LABELS.filter(([key]) => permissions[key]).map(
      ([, label]) => t(label)
    );
    return active.length ? active.join(', ') : '—';
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: 3,
        border: '1px solid rgba(15, 23, 42, 0.08)',
        bgcolor: '#F8FAFC',
      }}
    >
      {showTitle && (
        <Typography sx={{ fontWeight: 700, color: '#0F172A', mb: 2 }}>
          {t('User Management')}
        </Typography>
      )}

      {/* Zone 1: Invite trigger */}
      <Stack mb={2}>
        <Button
          variant="contained"
          size="small"
          onClick={() => setBulkInviteOpen(true)}
          style={{ alignSelf: 'flex-start' }}
        >
          {t('Invite New Users')}
        </Button>
      </Stack>
      <BulkInviteModal
        open={bulkInviteOpen}
        onClose={() => setBulkInviteOpen(false)}
        onSuccess={handleBulkInviteSuccess}
      />

      {/* Zone 2: Roster table */}
      <TableContainer
        component={Paper}
        sx={{
          width: '100%',
          overflowX: 'auto',
          borderRadius: 3,
          border: '1px solid rgba(17, 24, 39, 0.08)',
          boxShadow: '0 10px 28px rgba(15, 23, 42, 0.06)',
          bgcolor: '#FFFFFF',
        }}
      >
        <Table
          size="small"
          sx={{
            width: '100%',
            tableLayout: 'auto',
            '& .MuiTableHead-root .MuiTableRow-root': {
              bgcolor: '#FFFFFF',
            },
            '& .MuiTableHead-root .MuiTableCell-root': {
              color: '#5A6FAF',
              fontSize: '0.78rem',
              fontWeight: 700,
              textTransform: 'none',
              letterSpacing: '0.01em',
              borderBottom: '1px solid rgba(15, 23, 42, 0.08)',
              py: 1.5,
            },
            '& .MuiTableBody-root .MuiTableRow-root': {
              transition: 'background-color 120ms ease',
              '&:hover': {
                bgcolor: '#F9FBFF',
              },
            },
            '& .MuiTableBody-root .MuiTableCell-root': {
              borderBottom: '1px solid rgba(17, 24, 39, 0.06)',
              py: 1.25,
            },
          }}
        >
          <TableHead>
            <TableRow>
              <StyledTableColumnHeader transform="uppercase" fontWeight="bold">
                {t('Name / Email')}
              </StyledTableColumnHeader>
              <StyledTableColumnHeader transform="uppercase" fontWeight="bold">
                {t('Status')}
              </StyledTableColumnHeader>
              <StyledTableColumnHeader transform="uppercase" fontWeight="bold">
                {t('Permissions')}
              </StyledTableColumnHeader>
              <StyledTableColumnHeader
                transform="uppercase"
                fontWeight="bold"
                align="right"
              >
                {t('Actions')}
              </StyledTableColumnHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading
              ? Array.from({ length: 3 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell>
                      <Skeleton variant="text" width="60%" />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" width={60} />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" width="80%" />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" width={80} />
                    </TableCell>
                  </TableRow>
                ))
              : roster.map(entry => {
                  if (entry.entry_type === 'member') {
                    const member = entry as RosterMember;
                    const isProtectedAdmin = Boolean(
                      member.permissions.admin || member.permissions.super_admin
                    );
                    return (
                      <TableRow key={member.id}>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {member.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {member.email}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <StatusChip status="accepted" t={t} />
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">
                            {permissionSummary(member.permissions)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Stack
                            direction="row"
                            spacing={1}
                            justifyContent="flex-end"
                          >
                            <Tooltip title={t('Edit')} placement="top" arrow>
                              <span>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="inherit"
                                  padding="0.5rem"
                                  style={actionIconButtonStyle}
                                  aria-label={t('Edit')}
                                  onClick={() => handleOpenEdit(member)}
                                  disabled={isProtectedAdmin}
                                >
                                  <EditOutlined />
                                </Button>
                              </span>
                            </Tooltip>
                            <Tooltip
                              title={t('Remove User')}
                              placement="top"
                              arrow
                            >
                              <span>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="inherit"
                                  padding="0.5rem"
                                  style={actionIconButtonStyle}
                                  aria-label={t('Remove User')}
                                  onClick={() => setRemoveMember(member)}
                                  disabled={isProtectedAdmin}
                                >
                                  <DeleteOutline />
                                </Button>
                              </span>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  }

                  const invite = entry as RosterInvite;
                  return (
                    <TableRow key={invite.id}>
                      <TableCell>
                        <Typography variant="body2">{invite.email}</Typography>
                      </TableCell>
                      <TableCell>
                        <StatusChip status={invite.roster_status} t={t} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {permissionSummary(invite.permissions ?? {})}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Stack
                          direction="row"
                          spacing={1}
                          justifyContent="flex-end"
                        >
                          {invite.roster_status === 'pending' && (
                            <Tooltip title={t('Revoke')} placement="top" arrow>
                              <span>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="inherit"
                                  padding="0.5rem"
                                  style={actionIconButtonStyle}
                                  aria-label={t('Revoke')}
                                  onClick={() => handleRevoke(invite)}
                                >
                                  <CancelOutlined />
                                </Button>
                              </span>
                            </Tooltip>
                          )}
                          {invite.roster_status === 'expired' && (
                            <>
                              <Tooltip
                                title={t('Resend')}
                                placement="top"
                                arrow
                              >
                                <span>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    color="inherit"
                                    padding="0.5rem"
                                    style={actionIconButtonStyle}
                                    aria-label={t('Resend')}
                                    onClick={() => handleResend(invite)}
                                  >
                                    <ReplayOutlined />
                                  </Button>
                                </span>
                              </Tooltip>
                              <Tooltip
                                title={t('Delete')}
                                placement="top"
                                arrow
                              >
                                <span>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    color="inherit"
                                    padding="0.5rem"
                                    style={actionIconButtonStyle}
                                    aria-label={t('Delete')}
                                    onClick={() => handleDeleteInvite(invite)}
                                  >
                                    <DeleteOutline />
                                  </Button>
                                </span>
                              </Tooltip>
                            </>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Edit permissions dialog */}
      <Dialog open={!!editMember} onClose={() => setEditMember(null)}>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {t('Save Permissions')}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={0.5} mt={1}>
            {PERMISSION_LABELS.map(([key, label]) => (
              <FormControlLabel
                key={key}
                control={
                  <Checkbox
                    checked={Boolean(editPermissions[key])}
                    onChange={() => handlePermissionToggle(key)}
                    size="small"
                  />
                }
                label={t(label)}
              />
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setEditMember(null)}
            disabled={saving}
          >
            {t('Cancel')}
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={handleSavePermissions}
            disabled={saving}
          >
            {t('Save Permissions')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Remove member confirmation dialog */}
      <Dialog open={!!removeMember} onClose={() => setRemoveMember(null)}>
        <DialogTitle sx={{ fontWeight: 700 }}>{t('Remove User')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {t(
              'Are you sure you want to remove {{email}}? This action cannot be undone.',
              {
                email: removeMember?.email ?? '',
              }
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            color="inherit"
            size="small"
            onClick={() => setRemoveMember(null)}
            disabled={removing}
          >
            {t('Cancel')}
          </Button>
          <Button
            color="error"
            size="small"
            onClick={handleRemove}
            disabled={removing}
          >
            {t('Confirm Remove')}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default UserManagementPanel;
